import type {
  AudioProcessorOptions,
  Track,
  TrackProcessor,
} from "livekit-client";
import { clientLog } from "stoat.js";

import { CONFIGURATION } from "@revolt/common";

import {
  AdaptiveGainController,
  amplitudeToDb,
  dbToGain,
  rms,
  type AdaptiveGainOptions,
  type AdaptiveGainState,
} from "./adaptiveGain";

/**
 * Microphone processing chain: RNNoise + a level control that adjusts itself.
 *
 *   mic ─▶ RNNoise worklet ─▶ analyser ─▶ AGC gain ─▶ duck gain ─▶ limiter ─▶ out
 *
 * Why not `livekit-rnnoise-processor` as-is: LiveKit allows exactly one
 * processor per track, and its processor owns the whole chain — there is no
 * seam to insert the level control into. So we drive its worklet ourselves;
 * the WASM and the worklet are the same Apache-2.0 build, just served from our
 * own origin instead of a public CDN (see public/rnnoise/README.md).
 *
 * ⚠️ The analyser sits BEFORE the gain nodes. Measuring after them would feed
 * our own correction back into the controller, and the gain would run away.
 *
 * ⚠️ Nothing here is allowed to be fatal. If the worklet or the WASM fails to
 * load, the chain quietly continues without denoising — a person whose voice
 * disappears because of a failed download would have no way to understand why.
 */

/** How often the level is measured. 25 ms is a compromise: fast enough that
 *  the duck lifts within a syllable, cheap enough to run on every client. */
const POLL_MS = 25;

/** Time constants of the two gain nodes, seconds (setTargetAtTime tau). */
const AGC_TAU = 0.05;
const DUCK_OPEN_TAU = 0.01;
const DUCK_CLOSE_TAU = 0.15;

const loadedWorkletContexts = new WeakSet<BaseAudioContext>();

export interface SmartDenoiseOptions {
  /** Run RNNoise. Off = level control only. */
  denoise?: boolean;
  /** Run the adaptive level control. Off = plain RNNoise, as before. */
  adaptive?: boolean;
  /** Where DenoiserWorklet.js and rnnoise.wasm are served from. Must end in "/". */
  assetsUrl?: string;
  /** Called on every measurement — for meters in the UI. */
  onState?: (state: AdaptiveGainState) => void;
  /** Level-control settings; see AdaptiveGainOptions. */
  gain?: Partial<AdaptiveGainOptions>;
}

export class SmartDenoiseProcessor
  implements TrackProcessor<Track.Kind.Audio, AudioProcessorOptions>
{
  readonly name = "smart-denoise";
  processedTrack?: MediaStreamTrack;

  readonly #options: Required<
    Omit<SmartDenoiseOptions, "onState" | "gain">
  > &
    Pick<SmartDenoiseOptions, "onState">;
  readonly #controller: AdaptiveGainController;

  #opts?: AudioProcessorOptions;
  #source?: MediaStreamAudioSourceNode;
  #denoise?: AudioWorkletNode;
  #analyser?: AnalyserNode;
  #agc?: GainNode;
  #duck?: GainNode;
  #limiter?: DynamicsCompressorNode;
  #destination?: MediaStreamAudioDestinationNode;
  #timer?: ReturnType<typeof setInterval>;
  #buffer?: Float32Array;
  #lastTick = 0;
  /** Whether RNNoise is actually in the chain — it may have failed to load. */
  #denoiseActive = false;

  constructor(options: SmartDenoiseOptions = {}) {
    this.#options = {
      denoise: options.denoise ?? true,
      adaptive: options.adaptive ?? true,
      assetsUrl: options.assetsUrl || CONFIGURATION.RNNOISE_WORKLET_CDN_URL,
      onState: options.onState,
    };
    this.#controller = new AdaptiveGainController(options.gain);
  }

  /**
   * Apply new level-control settings to a running microphone.
   *
   * Deliberately not a rebuild: the sliders are dragged while someone is
   * talking, and swapping the processor there would drop the track and cut
   * them off mid-word.
   */
  setGainOptions(options: Partial<AdaptiveGainOptions>): void {
    this.#controller.setOptions(options);
  }

  static isSupported(): boolean {
    return typeof AudioWorkletNode !== "undefined";
  }

  /** True once the chain is built and RNNoise is really running. */
  get denoiseActive(): boolean {
    return this.#denoiseActive;
  }

  async init(opts: AudioProcessorOptions): Promise<void> {
    await this.#build(opts, false);
  }

  async restart(opts: AudioProcessorOptions): Promise<void> {
    await this.#build(
      { ...opts, audioContext: opts.audioContext ?? this.#opts?.audioContext },
      true,
    );
  }

  async destroy(): Promise<void> {
    this.#teardown();
  }

  async #build(opts: AudioProcessorOptions, restart: boolean): Promise<void> {
    if (!opts?.audioContext || !opts?.track) {
      throw new Error("audioContext and track are required");
    }
    if (restart) this.#teardown();

    this.#opts = opts;
    const ctx = opts.audioContext;

    if (this.#options.denoise) {
      this.#denoise = await this.#createDenoiseNode(ctx);
      this.#denoiseActive = !!this.#denoise;
    }

    // RNNoise is a 48 kHz model; the package resamples otherwise, and resampled
    // audio suppresses noticeably worse. Not every device honours this, hence
    // the catch — a rejected constraint is not a reason to lose the microphone.
    try {
      await opts.track.applyConstraints({ sampleRate: { exact: 48000 } });
    } catch {
      /* device kept its own rate; RNNoise resamples internally */
    }

    this.#source = ctx.createMediaStreamSource(new MediaStream([opts.track]));
    this.#destination = ctx.createMediaStreamDestination();

    let node: AudioNode = this.#source;
    if (this.#denoise) {
      node.connect(this.#denoise);
      node = this.#denoise;
    }

    if (this.#options.adaptive) {
      this.#analyser = ctx.createAnalyser();
      // ~21 ms of audio at 48 kHz — one syllable's worth of energy, which is
      // what the controller reasons about.
      this.#analyser.fftSize = 1024;
      this.#agc = ctx.createGain();
      this.#duck = ctx.createGain();
      this.#limiter = ctx.createDynamicsCompressor();
      // Catches the peaks the AGC lifts into the ceiling. Without it, turning a
      // quiet microphone up by 18 dB would clip on every loud consonant.
      this.#limiter.threshold.value = -3;
      this.#limiter.knee.value = 6;
      this.#limiter.ratio.value = 12;
      this.#limiter.attack.value = 0.003;
      this.#limiter.release.value = 0.1;

      node.connect(this.#analyser);
      this.#analyser.connect(this.#agc);
      this.#agc.connect(this.#duck);
      this.#duck.connect(this.#limiter);
      this.#limiter.connect(this.#destination);

      this.#buffer = new Float32Array(this.#analyser.fftSize);
      this.#controller.reset();
      this.#lastTick = now();
      this.#timer = setInterval(() => this.#tick(), POLL_MS);
    } else {
      node.connect(this.#destination);
    }

    this.processedTrack = this.#destination.stream.getAudioTracks()[0];
  }

  async #createDenoiseNode(
    ctx: AudioContext,
  ): Promise<AudioWorkletNode | undefined> {
    const base = this.#options.assetsUrl || "/rnnoise/";
    try {
      if (!loadedWorkletContexts.has(ctx)) {
        await ctx.audioWorklet.addModule(
          new URL(`${base}DenoiserWorklet.js`, location.href).toString(),
        );
        loadedWorkletContexts.add(ctx);
      }

      const response = await fetch(new URL(`${base}rnnoise.wasm`, location.href));
      if (!response.ok) throw new Error(`rnnoise.wasm: ${response.status}`);
      const rnnoiseBuffer = await response.arrayBuffer();

      return new AudioWorkletNode(ctx, "DenoiserWorklet", {
        processorOptions: { rnnoiseBuffer },
        numberOfInputs: 1,
        numberOfOutputs: 1,
      });
    } catch (error) {
      // Degrade, never disappear: the microphone keeps working un-denoised.
      clientLog("warn", "voice.denoise.unavailable", {
        error: error instanceof Error ? error.message : String(error),
      });
      return undefined;
    }
  }

  #tick(): void {
    const analyser = this.#analyser;
    const buffer = this.#buffer;
    const ctx = this.#opts?.audioContext;
    if (!analyser || !buffer || !ctx || !this.#agc || !this.#duck) return;

    const t = now();
    const dt = this.#lastTick ? t - this.#lastTick : POLL_MS;
    this.#lastTick = t;

    analyser.getFloatTimeDomainData(buffer);
    const state = this.#controller.push(amplitudeToDb(rms(buffer)), dt);

    const at = ctx.currentTime;
    this.#agc.gain.setTargetAtTime(dbToGain(state.gainDb), at, AGC_TAU);
    this.#duck.gain.setTargetAtTime(
      dbToGain(state.duckDb),
      at,
      // Opening fast and closing slowly is what keeps the first syllable
      // intact; the reverse order clips the start of every sentence.
      state.speaking ? DUCK_OPEN_TAU : DUCK_CLOSE_TAU,
    );

    this.#options.onState?.(state);
  }

  #teardown(): void {
    if (this.#timer) clearInterval(this.#timer);
    this.#timer = undefined;

    if (this.#denoise) {
      // The worklet holds the WASM heap; without DESTROY it leaks per restart.
      try {
        this.#denoise.port.postMessage({ message: "DESTROY" });
        this.#denoise.port.close();
      } catch {
        /* port already gone */
      }
      this.#denoise.disconnect();
    }

    this.#source?.disconnect();
    this.#analyser?.disconnect();
    this.#agc?.disconnect();
    this.#duck?.disconnect();
    this.#limiter?.disconnect();

    this.#denoise = undefined;
    this.#source = undefined;
    this.#analyser = undefined;
    this.#agc = undefined;
    this.#duck = undefined;
    this.#limiter = undefined;
    this.#destination = undefined;
    this.#buffer = undefined;
    this.#denoiseActive = false;
    this.processedTrack = undefined;
  }
}

function now(): number {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}
